import { Injectable, inject } from '@angular/core';
import { DV_TRACKER } from '@duocvuong/event-tracker/angular';

/** Loại đối tượng nhân viên tìm kiếm, và kiểu từ khóa. Từ khóa không bao giờ được ghi, chỉ ghi loại và độ dài. */
export type AdminSearchTarget = 'customer' | 'order' | 'product' | 'other';
export type AdminTermKind = 'phone' | 'name' | 'order_id' | 'sku' | 'text';

/**
 * Cầu nối tới SDK ghi hành vi của nhân viên trên trang admin (AdminAudit pha 3, docs/guides/admin-audit.md của repo DvEventSourcing).
 * Màn hình, thời gian ở lại và truy vết lệnh gọi API do SDK tự ghi theo router. Service này ghi các event riêng của admin.
 * Mọi hàm bọc try/catch: lỗi ghi nhận không bao giờ được làm hỏng thao tác của nhân viên. Khi `eventTracking.enabled = false` mọi hàm là no-op.
 */
@Injectable({ providedIn: 'root' })
export class TrackingService {
  private tracker = this.resolveTracker();

  /** Mở hoặc lọc lại một báo cáo (dữ liệu báo cáo vừa tải xong). Không ghi giá trị lọc, chỉ ghi tên các bộ lọc đã dùng. */
  reportViewed(reportId: string, filterNames: string[], rangeDays: number | undefined, loadMs: number, rowCount: number | undefined): void {
    this.safe(() => {
      const props: Record<string, unknown> = {
        report_id: reportId.slice(0, 100),
        filter_names: filterNames.slice(0, 20).map((n) => n.slice(0, 60)),
        load_ms: Math.max(0, Math.round(loadMs)),
      };
      if (rangeDays !== undefined && Number.isFinite(rangeDays) && rangeDays >= 0) props['range_days'] = Math.round(rangeDays);
      if (rowCount !== undefined && Number.isFinite(rowCount) && rowCount >= 0) props['row_count'] = Math.round(rowCount);
      this.tracker.track('admin_report_viewed', props);
    });
  }

  /** Nhân viên tìm khách, đơn hoặc sản phẩm. Chỉ ghi loại đối tượng, loại và độ dài từ khóa, số kết quả. */
  search(target: AdminSearchTarget, termKind: AdminTermKind, termLength: number, resultCount: number | undefined): void {
    this.safe(() => {
      const props: Record<string, unknown> = { target, term_kind: termKind, term_length: Math.max(0, Math.round(termLength)) };
      if (resultCount !== undefined && Number.isFinite(resultCount) && resultCount >= 0) props['result_count'] = Math.round(resultCount);
      this.tracker.track('admin_search', props);
    });
  }

  /** Route guard chặn vì thiếu quyền. */
  accessDenied(route: string, missingPermission?: string): void {
    this.safe(() => {
      const props: Record<string, unknown> = { route: route.split('?')[0].slice(0, 200) };
      if (missingPermission) props['missing_permission'] = missingPermission.slice(0, 100);
      this.tracker.track('admin_access_denied', props);
    });
  }

  /** Nhân viên bấm xuất dữ liệu (gọi API xuất). */
  exportRequested(kind: string, rowCount?: number): void {
    this.safe(() => {
      const props: Record<string, unknown> = { kind: kind.slice(0, 100) };
      if (rowCount !== undefined && Number.isFinite(rowCount) && rowCount >= 0) props['row_count'] = Math.round(rowCount);
      this.tracker.track('admin_export_requested', props);
    });
  }

  /** Đưa chính sách ghi event (object lấy từ config) vào SDK. null hoặc sai thì SDK tắt. */
  applyPolicy(raw: unknown): void {
    this.safe(() => this.tracker.applyPolicy(raw));
  }

  /** Gọi ở ĐẦU logout, trước khi xóa token, để event còn lại được gửi kèm đúng nhân viên. */
  flush(): void {
    this.safe(() => void this.tracker.flush());
  }

  get enabled(): boolean {
    return !!this.tracker?.enabled;
  }

  /** Không bao giờ ném lỗi: nếu SDK không khởi tạo được thì dùng bản rỗng, trang admin vẫn chạy. */
  private resolveTracker(): any {
    try {
      return inject(DV_TRACKER);
    } catch (e) {
      console.warn('[tracking] tắt do không khởi tạo được SDK', e);
      return { enabled: false, track: () => undefined, flush: () => Promise.resolve(), applyPolicy: () => undefined };
    }
  }

  private safe<T>(fn: () => T): T | undefined {
    if (!this.tracker?.enabled) return undefined;
    try {
      return fn();
    } catch (e) {
      console.warn('[tracking]', e);
      return undefined;
    }
  }
}
